import { useCallback } from 'react'
import { adminConfig } from '~/entrypoint/config'

export type GenericRpcMethods = Record<string, { input: unknown; output: unknown }>

export interface RpcCallOptions {
	/**
	 * Cancel the underlying fetch when the signal aborts. The returned
	 * promise rejects with `error.name === 'AbortError'` — typically thrown
	 * from React effects on cleanup.
	 */
	signal?: AbortSignal
}

/**
 * Untyped RPC primitive. Posts to /rpc with { method, input } envelope and
 * unwraps the response. `useAdminRpc` wraps this with the AdminRpcMethods
 * contract for typed call sites.
 */
export function useRpc<TMethods extends GenericRpcMethods>() {
	return useCallback(async <K extends keyof TMethods & string>(
		method: K,
		input: TMethods[K]['input'],
		options: RpcCallOptions = {},
	): Promise<TMethods[K]['output']> => {
		const res = await fetch('/rpc', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/json',
				// Placeholder in prod (worker /rpc auth reads the real token from
				// the CONTEMBER_TOKEN cookie); local root token in dev.
				'Authorization': `Bearer ${adminConfig.sessionToken}`,
			},
			body: JSON.stringify({ method, input }),
			credentials: 'include',
			signal: options.signal,
		})

		const text = await res.text()
		let body: any = null
		let parsed = false
		try {
			if (text) {
				body = JSON.parse(text)
				parsed = true
			}
		} catch {}

		if (!res.ok) {
			const message = body?.error?.message ?? `RPC ${method} failed: ${res.status}`
			const err = new Error(message) as Error & { type?: string; details?: unknown }
			err.type = body?.error?.type
			err.details = body?.error?.details
			throw err
		}

		// A 2xx whose body isn't JSON means /rpc didn't reach the worker — e.g.
		// the admin is served from a host that returns the SPA index.html for
		// unknown paths. Without this guard `body` stays null and callers either
		// crash on `result.x` or — worse — treat a no-op as success (a delete that
		// "succeeded" but changed nothing). Fail loudly instead of silently.
		if (!parsed) {
			throw new Error(
				`RPC ${method} nedostupné: server nevrátil JSON (HTTP ${res.status}, `
					+ `${res.headers.get('content-type') ?? 'bez content-type'}). `
					+ 'Administrace musí běžet na hostu, kde /rpc obsluhuje worker.',
			)
		}

		return body as TMethods[K]['output']
	}, [])
}
